import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance'; // Use the configured instance
import { isValid } from 'date-fns'; // Needed for robust date handling
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Avatar,
    FormControlLabel,
    Switch,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Card // Added for consistent theming
} from '@mui/material';
import TimelineForm from '../components/TimelineForm'; // Adjust path if needed
import FollowedTimelines from '../components/FollowedTimelines'; // Import the new component
import { useAuth } from '../context/AuthContext'; // To get basic user info like photo easily
import CaseDetailModal from '../components/CaseDetailModal';

const ProfilePage = () => {
    const { user: authUser, deleteAccount } = useAuth(); // Get basic user info from Auth context if needed
    const [userData, setUserData] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveStatus, setSaveStatus] = useState({ saving: false, error: '', success: '' });
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isProcessingFollow, setIsProcessingFollow] = useState(false);
    const [refreshFollowedList, setRefreshFollowedList] = useState(0);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Fetch both user profile and timeline data concurrently
            const [userResponse, timelineResponse] = await Promise.all([
                axiosInstance.get('/api/user'),
                axiosInstance.get('/api/timeline/mine') // Fetch timeline data
            ]);

            const fetchedUser = userResponse.data.user || {};
            const fetchedTimeline = timelineResponse.data || {}; // Assuming timeline data is top-level in response
            // Check if a real timeline record exists (assuming backend sends _id for saved records)
            const hasActualTimelineData = fetchedTimeline && fetchedTimeline._id;

            setUserData(fetchedUser); // Store the raw fetched user data (profile part)

            // Define all expected fields and their types/defaults
            const expectedFields = {
                // ProfilePage fields
                displayName: '',
                email: '',
                role: 'member',
                membershipTier: 'free',
                // TimelineForm fields
                applicationStream: '',
                voOffice: '',
                isExpressEntry: false,
                isPnp: false,
                itaDate: null,
                nocCode: '',
                pnpProvince: '',
                pnpNominationDate: null,
                securityCheck: false,
                birthCountry: '',
                applicationSubmittedDate: null,
                aorDate: null,
                biometricsInstructionLetterDate: null,
                biometricsSubmittedDate: null,
                backgroundCheckStartDate: null,
                finalDecisionDate: null,
                portal1Date: null,
                portal2Date: null,
                eCoprDate: null,
                notes: '',
                hideEmail: false, // Added for email visibility
              };
              const profileFieldKeys = ['displayName', 'email', 'role', 'membershipTier']; // Define profile keys
              const timelineFieldKeys = Object.keys(expectedFields).filter(k => !profileFieldKeys.includes(k) && k !== 'googleId');

            // Initialize form data with defaults
            let initialFormData = { ...expectedFields };

            // --- Populate profile fields from fetchedUser ---
            profileFieldKeys.forEach(key => {
                const value = fetchedUser[key];
                 if (value !== undefined && value !== null) {
                     if (typeof expectedFields[key] === 'string') {
                         initialFormData[key] = String(value);
                     } else {
                         initialFormData[key] = value;
                     }
                 }
            });
             if (fetchedUser.email) initialFormData.email = fetchedUser.email;

            // --- Populate timeline fields ONLY if actual timeline data exists ---
            if (hasActualTimelineData) {
                timelineFieldKeys.forEach(key => {
                    const value = fetchedTimeline[key];
                    if (value !== undefined && value !== null) {
                        if (key.endsWith('Date')) {
                            const parsedDate = new Date(value);
                            initialFormData[key] = isValid(parsedDate) ? parsedDate : null;
                        } else if (typeof expectedFields[key] === 'boolean') {
                            initialFormData[key] = Boolean(value);
                        } else if (typeof expectedFields[key] === 'string') {
                            initialFormData[key] = String(value);
                        } else {
                            initialFormData[key] = value;
                        }
                    }
                });
            }

            setFormData(initialFormData);

        } catch (err) {
            console.error("Error fetching profile or timeline data:", err);
            let errorMsg = 'Failed to load profile data. Please try refreshing.';
            if (err.response && err.response.status === 401) {
                errorMsg = 'Unauthorized. Please login.';
            } else if (Array.isArray(err)) {
                 errorMsg = 'Failed to load some profile data. Please try refreshing.';
                 err.forEach(e => console.error("Individual fetch error:", e));
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleChange = useCallback((event) => {
        let name, value;
        if (event.target && typeof event.target === 'object' && 'name' in event.target && 'value' in event.target) {
            name = event.target.name;
            value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        } else if (event.target && typeof event.target === 'object' && 'name' in event.target && event.target.value instanceof Date) {
             name = event.target.name;
             value = event.target.value;
        } 
        
        if (name !== undefined) {
            if (name === 'hideEmail' && event.target.type === 'checkbox') {
                setFormData(prevData => ({
                    ...prevData,
                    [name]: !event.target.checked
                }));
            } else {
                setFormData(prevData => ({
                    ...prevData,
                    [name]: value
                }));
            }
        } else {
            console.warn("handleChange received an event it couldn't process:", event);
        }
    }, []);

    const handleSave = async () => {
        setSaveStatus({ saving: true, error: '', success: '' });

        const profileFields = ['displayName'];
        const timelineFields = [
            'applicationStream', 'voOffice', 'isExpressEntry', 'isPnp', 'itaDate',
            'nocCode', 'pnpProvince', 'pnpNominationDate', 'securityCheck', 'birthCountry',
            'applicationSubmittedDate', 'aorDate', 'biometricsInstructionLetterDate',
            'biometricsSubmittedDate', 'backgroundCheckStartDate', 'finalDecisionDate',
            'portal1Date', 'portal2Date', 'eCoprDate', 'notes',
            'hideEmail'
          ];

        const profilePayload = {};
        const timelinePayload = {};

        Object.keys(formData).forEach(key => {
            let value = formData[key];
            if (value instanceof Date) {
                try {
                    value = value.toISOString();
                } catch (dateError) {
                    console.error(`Error formatting date for key ${key}:`, dateError);
                    value = null;
                }
            }
            if (profileFields.includes(key)) {
                profilePayload[key] = value;
            } else if (timelineFields.includes(key)) {
                timelinePayload[key] = value;
            }
        });

        try {
            const [profileResponse, timelineResponse] = await Promise.all([
              axiosInstance.put('/api/user/profile', profilePayload),
              axiosInstance.post('/api/timeline/', timelinePayload)
            ]);
        
            const updatedUser = profileResponse.data.user || {};
            const updatedTimeline = timelineResponse.data || {};

            let newFormData = { ...formData };
            profileFields.forEach(key => {
                if (updatedUser.hasOwnProperty(key)) newFormData[key] = updatedUser[key];
            });
            timelineFields.forEach(key => {
                 if (updatedTimeline.hasOwnProperty(key)) {
                     const value = updatedTimeline[key];
                     if (key.endsWith('Date') && typeof value === 'string') {
                         const parsedDate = new Date(value);
                         newFormData[key] = isValid(parsedDate) ? parsedDate : null;
                     } else {
                         newFormData[key] = value;
                     }
                 }
            });
            if (updatedUser.email) newFormData.email = updatedUser.email;

            setUserData(updatedUser);
            setFormData(newFormData);

            setSaveStatus({ saving: false, error: '', success: 'Profile saved successfully!' });
            setTimeout(() => setSaveStatus(prev => ({ ...prev, success: '' })), 3000);

        } catch (err) {
            console.error("Error saving profile or timeline data:", err);
            let errorMsg = 'Failed to save profile. Please try again.';
             if (err.response?.data?.error) {
                 errorMsg = err.response.data.error;
             } else if (Array.isArray(err)) {
                 errorMsg = 'Failed to save some profile data. Please try again.';
                 err.forEach(e => console.error("Individual save error:", e));
             }
            setSaveStatus({ saving: false, error: errorMsg, success: '' });
        }
    };

    const handleDeleteAccount = async () => {
        const { success, message } = await deleteAccount();
        if (success) {
            console.log('Account deletion initiated.');
        } else {
            setSaveStatus({ saving: false, error: message, success: '' });
        }
        setIsDeleteDialogOpen(false);
    };

    const handleTimelineSelect = async (timeline) => {
        if (!timeline?._id) return;
        try {
            const response = await axiosInstance.get(`/api/timeline/${timeline._id}`);
            setSelectedCase(response.data);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Failed to fetch timeline details", error);
            setError('Could not load timeline details. Please try again.');
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCase(null);
    };

    const handleFollowToggle = async (caseId, isCurrentlyFollowing) => {
        if (!caseId) return;
        setIsProcessingFollow(true);
        try {
            if (isCurrentlyFollowing) {
                await axiosInstance.post(`/api/user/timelines/unfollow/${caseId}`);
            } else {
                await axiosInstance.post(`/api/user/timelines/follow/${caseId}`);
            }
            setRefreshFollowedList(prev => prev + 1);
            handleCloseModal();
        } catch (error) {
            console.error("Failed to toggle follow status", error);
            setSaveStatus(prev => ({ ...prev, error: 'Could not update follow status.' }));
        } finally {
            setIsProcessingFollow(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    if (!userData) {
        return <Alert severity="warning" sx={{ m: 2 }}>No user data found.</Alert>;
    }

    const tiers = ["free", "premium", "pro"];

    const formatRoleForDisplay = (role) => {
        if (!role) return 'Member';
        return role.charAt(0).toUpperCase() + role.slice(1);
    };

    return (
        <Box sx={{ padding: { xs: 0, sm: 2 } }}>
            <Card sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    {authUser?.photo ? (
                       <Avatar src={authUser.photo} alt={formData.displayName || ''} sx={{ mr: 1.5, width: 40, height: 40 }} />
                    ) : (
                       <Avatar sx={{ mr: 1.5, width: 40, height: 40 }}>{formData.displayName ? formData.displayName.charAt(0).toUpperCase() : 'U'}</Avatar>
                    )}
                    Edit Profile
                </Typography>

                <Box component="form" noValidate autoComplete="off">
                    <Grid container spacing={2}>
                        {/* Basic Info */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                margin="dense"
                                size="small"
                                label="Display Name"
                                name="displayName"
                                value={formData.displayName || ''}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                             <TextField
                                fullWidth
                                disabled
                                margin="dense"
                                size="small"
                                label="Email"
                                name="email"
                                value={formData.email || ''}
                            />
                        </Grid>

                         {/* Role (Read-Only) & Membership Tier (Editable) */}
                         <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                disabled
                                margin="dense"
                                size="small"
                                label="Role"
                                name="role"
                                value={formatRoleForDisplay(formData.role)}
                                variant="outlined"
                            />
                        </Grid>
                        {/* Membership is masked for now from UI, Later we can add a tier upgrade option */}
                        {/*
                         <Grid item xs={12} sm={6}>
                             <FormControl fullWidth margin="dense" size="small">
                                <InputLabel id="membershipTier-label">Membership Tier</InputLabel>
                                <Select
                                    labelId="membershipTier-label"
                                    name="membershipTier"
                                    disabled
                                    value={formData.membershipTier || 'free'}
                                    label="Membership Tier"
                                    variant="outlined"
                                >
                                    {tiers.map((tier) => (
                                        <MenuItem key={tier} value={tier}>
                                            {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        */}

                        {/* Email Visibility Setting */}
                        <Grid item xs={12} sx={{ pt: 1, pb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.hideEmail !== undefined ? !formData.hideEmail : true}
                                        onChange={handleChange}
                                        name="hideEmail"
                                        color="primary"
                                    />
                                }
                                label={formData.hideEmail ? "Email Hidden in public details" : "Email Visible in public details"}
                            />
                        </Grid>

                    </Grid>
                    
                    {formData && Object.keys(formData).length > 0 && (
                       <TimelineForm formData={formData} onChange={handleChange} />
                    )}

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleSave}
                            disabled={saveStatus.saving}
                        >
                            {saveStatus.saving ? <CircularProgress size={24} color="inherit" /> : 'Save Profile'}
                        </Button>
                        {saveStatus.error && (
                            <Alert severity="error" sx={{ mt: 2 }}>{saveStatus.error}</Alert>
                        )}
                         {saveStatus.success && (
                            <Alert severity="success" sx={{ mt: 2 }}>{saveStatus.success}</Alert>
                        )}
                    </Box>
                </Box>

                <FollowedTimelines key={refreshFollowedList} onTimelineSelect={handleTimelineSelect} />

                <Divider sx={{ my: 3 }} />

                {userData?.expireAt ? (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid orange', borderRadius: 1 }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Account Deletion Pending
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Your account is scheduled for permanent deletion on {new Date(userData.expireAt * 1000).toLocaleDateString()}.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid red', borderRadius: 1 }}>
                        <Typography variant="h6" color="error" gutterBottom>
                            Delete Account
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Once you request deletion, your user data is set for permanent deletion in 30 days. This action cannot be undone.
                        </Typography>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setIsDeleteDialogOpen(true)}
                        >
                            Request Account Deletion
                        </Button>
                    </Box>
                )}

                <Dialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Confirm Account Deletion"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to request the deletion of your account?
                            <br /><br />
                            Your user data is set for deletion in 30 days. This action cannot be reversed. Meanwhile, you can still login and access your data.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDeleteAccount} color="error" autoFocus>
                            Delete My Account
                        </Button>
                    </DialogActions>
                </Dialog>

                {selectedCase && (
                    <CaseDetailModal
                        open={isModalOpen}
                        onClose={handleCloseModal}
                        caseData={selectedCase}
                        isFollowing={true}
                        isProcessingFollow={isProcessingFollow}
                        onFollowToggle={handleFollowToggle}
                    />
                )}
            </Card>
        </Box>
    );
};

export default ProfilePage;